import { createReducer, on } from '@ngrx/store';
import { LocationFacebookTokenPair, SocialSharingTemplateSettings, FacebookAuthResult } from '@models/index';
import { FacebookActions } from '@actions/index';
import { SocialSharingScheduleSettings, SocialSharingScheduleType } from '@models/socialSharingScheduleSettings.model';

import { environment } from '@environments/environment';
import { FacebookErrorModel } from '@models/facebookError.model';
import { emptyFacebookErrorModel } from '@utilities/data-utils';

export interface State {
  pages: LocationFacebookTokenPair[];
  scheduleSettings: SocialSharingScheduleSettings | null;
  templateSettings: SocialSharingTemplateSettings | null;
  isExecutingTask: boolean;
  selectedToken: LocationFacebookTokenPair[];
  error: string | null;
  shareReviewSuccess: true | null;
  authResult: FacebookAuthResult;
  isOpenModal: Boolean;
  authSuccess: boolean;
  commentToBePosted: {
    message: string, 
    postId: string,
    pageId: string
  };
  facebookError: FacebookErrorModel
}

const initialState: State = {
  pages: [],
  scheduleSettings: null,
  templateSettings: null,
  isExecutingTask: true,
  selectedToken: [],
  error: null,
  shareReviewSuccess: null,
  authResult: null,
  isOpenModal: false,
  authSuccess: false,
  commentToBePosted: {
    message: '', 
    postId: '',
    pageId: ''
  },
  facebookError: emptyFacebookErrorModel()
};

const reduceStartTask = (state: State, _) => ({
  ...state,
  isExecutingTask: true,
})

const reducePageUpdate = (state: State, update: LocationFacebookTokenPair) => ({
  ...state,
  pages: state.pages.slice().reduce((pages, each) => {
    return pages.concat(each.locationToken.id === update.locationToken.id ? update : each)
  }, []),
  isExecutingTask: false,
})

const reduceScheduleSettingsUpdate = (state: State, { settings }) => ({
  ...state,
  scheduleSettings: settings,
  isExecutingTask: false,
})

const reduceTemplateSettingsUpdate = (state: State, { settings }) => {
  if(environment.showConsole)console.log('[social-sharing.reducer templateSettingsUpdate]', 'settings:', settings)
  return {
    ...state,
    templateSettings: settings,
    isExecutingTask: false,
  }
}

// making State generic explicit is needed (not sure why)
// otherwise the State has `pages: any[]` for some reason and loses typing
export const reducer = createReducer<State>(
  initialState,
  on(FacebookActions.reset, _ => ({ ...initialState })),
  on(FacebookActions.error, (state, error) => {
    return {
      ...state,
      error: error ? error.message : null,
      isExecutingTask: false,
    }
  }),
  on(FacebookActions.searchFacebookPagesByLocation, reduceStartTask),
  on(FacebookActions.searchFacebookPagesByLocationSuccess, (state, { pages }) => ({
    ...state,
    pages,
    isExecutingTask: false,
  })),
  on(FacebookActions.openSelectPagesModal, (state, { isOpen }) => {
    return ({
    ...state,
    isOpenModal: isOpen,
    isExecutingTask: false
  })}),
  on(FacebookActions.saveAuthToken, (state, { authResult }) => {
    return ({
    ...state,
    authResult: authResult,
    authSuccess:true,
    isExecutingTask: false
  })}),
  on(FacebookActions.enableFacebookPage, reduceStartTask),
  on(FacebookActions.enableFacebookPageSuccess, reducePageUpdate),
  on(FacebookActions.disableFacebookPage, reduceStartTask),
  on(FacebookActions.disableFacebookPageSuccess, reducePageUpdate),
  on(FacebookActions.removeFacebookPage, reduceStartTask),
  on(FacebookActions.removeFacebookPageSuccess, (state, removed) => ({
    ...state,
    selectedToken: state.selectedToken.filter(page => {
      return !(page.locationToken.id == removed.locationTokenId && page.locationToken.locationId == removed.locationId)
    }),
    isExecutingTask: false,
  })),
  on(FacebookActions.searchScheduleSettings, reduceStartTask),
  on(FacebookActions.searchScheduleSettingsSuccess, reduceScheduleSettingsUpdate),
  on(FacebookActions.updateScheduleSettings, reduceStartTask),
  on(FacebookActions.updateScheduleSettingsSuccess, reduceScheduleSettingsUpdate),
  on(FacebookActions.searchTemplateSettings, reduceStartTask),
  on(FacebookActions.searchTemplateSettingsSuccess, reduceTemplateSettingsUpdate),
  on(FacebookActions.updateTemplateSettings, reduceStartTask),
  on(FacebookActions.updateTemplateSettingsSuccess, reduceTemplateSettingsUpdate),
  on(FacebookActions.authenticateFacebook, reduceStartTask),
  on(FacebookActions.setupPagesFromAuthResultForLocation, reduceStartTask),
  on(FacebookActions.shareReview, (state) => ({
    ...state,
    isExecutingTask: true,
  })),
  on(FacebookActions.setSelectedTokens, (state, {selectedTokens}) => ({
    ...state,
    selectedToken: selectedTokens
  })),
  on(FacebookActions.shareReviewSuccess, (state) => ({
    ...state,
    isExecutingTask: false,
    shareReviewSuccess: true,
  })),
  on(FacebookActions.clearState, (state) => {
    return ({ ...state, 
        selectedToken: [],
    })}),

  on(FacebookActions.setAuthState, (state, { authState }) => {
      return ({
      ...state,
      authSuccess: authState
    })}),
    on(FacebookActions.updateCommentOnFacebookPost, (state, {request}) => ({ ...state, commentToBePosted: {postId: request.payload.postId, message: request.payload.messgae, pageId: request.payload.pageId}})),
    on(FacebookActions.updateCommentOnFacebookPostSuccess, (state, {review}) => ({ ...state, commentToBePosted: {postId: '', message: '', pageId: ''} })),
    on(FacebookActions.setFacebookError, (state, {error}) => ({
      ...state,
      facebookError: error,
    })),

  on(FacebookActions.commentFacebookPost, (state, {request}) => ({ ...state, commentToBePosted: {postId: request.payload.postId, message: request.payload.messgae, pageId: request.payload.pageId}})),

  on(FacebookActions.commentFacebookPostSuccess, (state, _) => ({ ...state, commentToBePosted: {postId: '', message: '', pageId: ''} })),
);


export const getPages = (state: State) => state.pages;
export const getIsOpenModal = (state: State) => state.isOpenModal;
export const getScheduleSettings = (state: State) => state.scheduleSettings;
export const getTemplateSettings = (state: State) => state.templateSettings;
export const getIsExecutingTask = (state: State) => state.isExecutingTask;
export const getSelectedToken = (state: State) => state.selectedToken
export const getError = (state: State) => state.error;
export const getShareReviewSuccess = (state: State) => state.shareReviewSuccess;
export const getAuthToken = (state: State) => state.authResult;
export const getAuthState = (state: State) => state.authSuccess;
export const getCommentToBePosted = (state: State) => state.commentToBePosted;
export const getFacebookError = (state: State) => state.facebookError;
